import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { SelectField } from "@/components/ui/select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FileText, Plus, Search, Edit, Trash2, Calendar, RefreshCw, Save } from "lucide-react";

interface AppContent {
  _id: string;
  type: string;
  data: string;
  createdAt: string;
  updatedAt: string;
}

const CONTENT_TYPES = [
  "privacy_policy",
  "terms_of_service",
  "about_us",
  "contact_info",
  "help_center",
  "faq_content",
  "welcome_message",
  "app_description",
] as const;

export default function Content() {
  const { currentTheme } = useTheme();
  const [contents, setContents] = React.useState<AppContent[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedType, setSelectedType] = React.useState<string>("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [selectedContent, setSelectedContent] = React.useState<AppContent | null>(null);
  const [formData, setFormData] = React.useState({
    type: "",
    data: "",
  });

  // Load contents
  const loadContents = async (filter?: string) => {
    setLoading(true);
    try {
      console.log("Loading content with filter:", filter);
      const response = await apiService.getAllContent({ filter });
      console.log("Content API response:", response);
      if (response.type === 0) {
        setContents(response.data as AppContent[]);
        console.log("Content loaded:", response.data?.length || 0, "items");
      } else {
        console.error("Content API error:", response.message);
        toast.error(response.message || "Failed to load content");
      }
    } catch (error) {
      console.error("Content API catch error:", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    void loadContents();
  }, []);

  // Create content
  const handleCreateContent = async () => {
    if (!formData.type || !formData.data.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      console.log("Creating content with data:", formData);
      const response = await apiService.createContent(formData);
      console.log("Create content response:", response);
      if (response.type === 0) {
        toast.success("Content created successfully");
        setIsCreateDialogOpen(false);
        setFormData({ type: "", data: "" });
        void loadContents();
      } else {
        console.error("Create content error:", response.message);
        toast.error(response.message || "Failed to create content");
      }
    } catch (error) {
      console.error("Create content catch error:", error);
      toast.error("Failed to create content");
    }
  };

  // Update content
  const handleUpdateContent = async () => {
    if (!selectedContent || !formData.type || !formData.data.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await apiService.updateContent(formData);
      if (response.type === 0) {
        toast.success("Content updated successfully");
        setIsEditDialogOpen(false);
        setSelectedContent(null);
        setFormData({ type: "", data: "" });
        void loadContents();
      } else {
        toast.error(response.message || "Failed to update content");
      }
    } catch {
      toast.error("Failed to update content");
    }
  };

  // Delete content
  const handleDeleteContent = async (type: string) => {
    if (!confirm("Are you sure you want to delete this content?")) return;

    try {
      const response = await apiService.deleteContent(type);
      if (response.type === 0) {
        toast.success("Content deleted successfully");
        void loadContents();
      } else {
        toast.error(response.message || "Failed to delete content");
      }
    } catch {
      toast.error("Failed to delete content");
    }
  };

  // Edit content
  const handleEditContent = (content: AppContent) => {
    setFormData({
      type: content.type,
      data: content.data,
    });
    setSelectedContent(content);
    setIsEditDialogOpen(true);
  };

  // Filter contents
  const filteredContents = contents.filter(content => {
    const matchesSearch =
      content.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.data.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || content.type === selectedType;
    return matchesSearch && matchesType;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContentTypeLabel = (type: string) => {
    return type
      .split("_")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const truncateText = (text: string, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const resetForm = () => {
    setFormData({ type: "", data: "" });
    setSelectedContent(null);
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Management</h1>
          <p className="text-muted-foreground">Manage application content and static pages</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadContents();
            }}
            variant="outline"
            size="sm"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: currentTheme.colors.primary }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[100vw] max-w-6xl max-h-[92vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>Add New Content</DialogTitle>
                <DialogDescription>
                  Create new application content. You can use HTML formatting in the content field.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="contentType">Content Type *</Label>
                  <SelectField
                    value={formData.type}
                    onValueChange={(value: string) =>
                      setFormData(prev => ({ ...prev, type: value }))
                    }
                    placeholder="Select content type"
                    options={CONTENT_TYPES.map(type => ({
                      label: getContentTypeLabel(type),
                      value: type,
                    }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contentData">Content *</Label>
                  <div className="bg-white rounded border pb-4">
                    <ReactQuill
                      theme="snow"
                      value={formData.data}
                      onChange={(value: string) => setFormData(prev => ({ ...prev, data: value }))}
                      style={{ height: 560 }}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateContent}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                  disabled={!formData.type || !formData.data.trim()}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Create Content
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search content by type or content..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
            <div className="w-48">
              <SelectField
                value={selectedType}
                onValueChange={(value: string) => {
                  setSelectedType(value);
                }}
                placeholder="Filter by type"
                options={[
                  { label: "All Types", value: "" },
                  ...CONTENT_TYPES.map(type => ({
                    label: getContentTypeLabel(type),
                    value: type,
                  })),
                ]}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content List */}
      <Card>
        <CardHeader>
          <CardTitle>Application Content</CardTitle>
          <CardDescription>
            {filteredContents.length} content item
            {filteredContents.length !== 1 ? "s" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              <span>Loading content...</span>
            </div>
          ) : filteredContents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">No content found</p>
              <p className="text-sm text-muted-foreground">
                {searchTerm || selectedType
                  ? "Try adjusting your search or filters"
                  : "Get started by adding your first content item"}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredContents.map(content => (
                <div
                  key={content._id}
                  className="border rounded-lg p-4 hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <FileText className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            {getContentTypeLabel(content.type)}
                          </h3>
                          <div className="flex items-center space-x-2 mt-1">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              Updated: {formatDate(content.updatedAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-13">
                        <div className="text-sm text-muted-foreground prose prose-sm max-w-none">
                          {truncateText(content.data, 200)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Edit"
                        onClick={() => {
                          handleEditContent(content);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Delete"
                        className="text-red-600"
                        onClick={() => {
                          void handleDeleteContent(content.type);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Content Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="w-[100vw] max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
            <DialogDescription>
              Update application content. You can use HTML formatting in the content field.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="editContentType">Content Type *</Label>
              <SelectField
                value={formData.type}
                onValueChange={(value: string) => setFormData(prev => ({ ...prev, type: value }))}
                placeholder="Select content type"
                options={CONTENT_TYPES.map(type => ({
                  label: getContentTypeLabel(type),
                  value: type,
                }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="editContentData">Content *</Label>
              <div className="bg-white rounded border">
                <ReactQuill
                  theme="snow"
                  value={formData.data}
                  onChange={(value: string) => setFormData(prev => ({ ...prev, data: value }))}
                  style={{ height: 500 }}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateContent}
              style={{ backgroundColor: currentTheme.colors.primary }}
              disabled={!formData.type || !formData.data.trim()}
            >
              <Save className="w-4 h-4 mr-2" />
              Update Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
